// src/pages/LoanDues.tsx
import { Icon } from "@iconify/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { AxiosError } from "axios";
import toast from "react-hot-toast";

import Button from "../../components/Button";
import InputField from "../../components/InputField";
import { useAuth } from "../../context/AuthContext";
import Logo from "../../assets/images/Logo.png";
import {
  deleteLoan,
  getBalanceDue,
  getLoan,
  getloanSettlementReceipt,
  getTransactionReceipt,
  settlement as settleLoan,
} from "../../services/loanService";

/* child components */
import DuePaymentsTable from "../../components/LoanDatas/DuePaymentsTable";
import TransactionModal from "../../components/LoanDatas/TransactionModal";
import SettlementModal from "../../components/LoanDatas/SettlementModal";
import DeleteConfirmModal from "../../components/LoanDatas/DeleteConfirmModal";
import LoanDocuments from "../../components/LoanDatas/LoanDocuments";
import LoanNominees from "../../components/LoanDatas/LoanNominees";
import LoanTopCards from "../../components/LoanDatas/LoanTopCards";
import EditLoanModal from "../../components/LoanDatas/EditLoanModal";
import OneVehicleDetails, {
  VehicleShape,
} from "../../components/ProfileDatas/OneVehicleDetails";
import { getVehicle } from "../../services/vehicleService";
import Modal from "../../components/Modal";
import html2pdf from "html2pdf.js";

/* -------------------------
   Exported types & helpers
   (child components import types/helpers from this file)
   ------------------------- */

export type ApiSchedule = {
  schedule_id: number;
  loan_id: number;
  due_date: string;
  principal_amount: string;
  interest_amount: string;
  total_due: string;
  payment_status: string;
  payment_on?: string | null;
  paid_amount: string;
  pending_amount: string;
};

export type Borrower = {
  profile_id: number;
  first_name?: string | null;
  last_name?: string | null;
  customer_code?: string | null;
  profile_photo?: string | null;
  phone?: string | null;
  status?: string | null;
  profile_photo_url?: any;
  mobile_number?: string | number;
};

export type Vehicle = {
  manufacturer?: string | null;
  model?: string | null;
  variant?: string | null;
  registration_number?: string | null;
  vehicle_id?: number;
  year_of_manufacture?: any;
};

export type Transaction = {
  transaction_id: number;
  loan_id: number;
  schedule_id: number;
  user_id: number;
  processed_by?: number | null;
  amount_paid: string;
  payment_mode: string;
  reference_number?: string | null;
  status: string;
  created_at: string;
  updated_at?: string;
  processed_by_role?: any;
  additional_amount?: any;
  adjustment_amount?: any;
  processed_by_id?: any;
};

export type LoanApiResponse = {
  loan_id: string | number;
  loan_amount: string | number;
  loan_created_date: string;
  created_by_id: string | number;
  created_by_role: string | number;
  disbursement_date?: string;
  interest_rate?: string | number;
  tenure_months?: number;
  emi_amount?: string | number;
  total_amount?: string | number;
  start_date?: string;
  end_date?: string;
  status?: string;
  borrower?: Borrower;
  vehicle?: Vehicle;
  schedules?: ApiSchedule[];
  transactions?: Transaction[];
  loan_code?: string | number;
  document_charges?: any;
  notes?: any;
  profile_photo_url?: any;
};

export type PaymentStatus =
  | "Paid"
  | "Upcoming"
  | "Partially Paid"
  | "Late"
  | "Late Paid";

export type Payment = {
  dueDate: string;
  amount: string; // formatted display
  status: PaymentStatus;
  paymentDate?: string | null;
  raw?: ApiSchedule;
  remainingAmount?: string;
  remainingNumeric?: number;
  totalPaid?: string | number;
};

export type Settlement = {
  loan_id: number;
  loan_code: any;
  // total_due: number | null;
  settlement_amount: number | null;
};

export const fmtCurrency = (val?: string | number | null) => {
  if (val == null || val === "") return "—";
  const n = Number(val);
  if (Number.isNaN(n)) return String(val);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(n);
};

export const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

/* ---------- Local helpers ---------- */

const mapPaymentStatus = (
  apiStatus?: string,
  paidAmount?: string | number | null,
  totalDue?: string | number | null,
): Exclude<PaymentStatus, "Late"> => {
  const api = String(apiStatus ?? "").trim();
  const s = api.toLowerCase();
  const paid = Number(paidAmount ?? 0);
  const total = Number(totalDue ?? 0);

  // honor explicit "Late Paid" from API first (handle variants)
  if (s === "late paid" || s === "late_paid" || s === "late-paid") {
    return "Late Paid";
  }

  // fallback for non-numeric cases
  if (!Number.isFinite(paid) || !Number.isFinite(total)) {
    if (s === "paid") return "Paid";
    if (s === "pending") return "Upcoming";
    return "Upcoming";
  }

  // numeric-driven decisions
  if (total > 0 && paid >= total) return "Paid";
  if (paid > 0 && paid < total) return "Partially Paid";
  if (s === "pending") return "Upcoming";

  return "Upcoming";
};

/* ---------- Component ---------- */

export default function LoanDues() {
  const location = useLocation();
  const { user } = useAuth();
  const { loan_id } = (location.state as any) || {};
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  // main data state
  const [loan, setLoan] = useState<LoanApiResponse | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<
    "due" | "docs" | "nominees" | "notes"
  >("due");

  // Transaction modal state
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [selectedTransactions, setSelectedTransactions] = useState<
    Transaction[]
  >([]);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloading, setDownloading] = useState(false);

  // settlement state
  const [showSettlement, setShowSettlement] = useState(false);
  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const [discountStr, setDiscountStr] = useState<string>("0");
  const [additionalStr, setAdditionalStr] = useState<string>("0");
  const [paidOnStr, setPaidOnStr] = useState<string>(() => {
    const t = new Date();
    const yyyy = t.getFullYear();
    const mm = String(t.getMonth() + 1).padStart(2, "0");
    const dd = String(t.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  });
  const [processingSettlement, setProcessingSettlement] = useState(false);
  const [settlementError, setSettlementError] = useState<string | null>(null);
  const [settlementSuccess, setSettlementSuccess] = useState<string | null>(
    null,
  );

  // delete-confirm modal state
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);

  // vehicle modal state
  const [showVehicleModal, setShowVehicleModal] = useState(false);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [vehicleError, setVehicleError] = useState<string | null>(null);
  const [vehicleData, setVehicleData] = useState<VehicleShape | null>(null);

  // edit loan modal state
  const [showEditLoanModal, setShowEditLoanModal] = useState(false);

  const isAdmin = user?.role === "Admin";
  const isCustomer = user?.role === "Customer";
  const pdfRef = useRef<HTMLDivElement>(null);
  const pdfRefSettlement = useRef<HTMLDivElement>(null);
  const [loanSettlement, setLoanSettlement] = useState<any>(null);
  const loanSettlementRef = useRef<any>(null);
  const settlementloan = loanSettlementRef.current || loanSettlement;
  const [isDownloading, setIsDownloading] = useState(false);

  /* ---------- Load loan + build payments ---------- */
  useEffect(() => {
    if (!loan_id) {
      setError("No loan_id provided.");
      return;
    }

    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getLoan(loan_id);
        if (!mounted) return;
        if (!res.success) {
          setError(res.message ?? "Failed to load loan");
          setLoading(false);
          return;
        }
        const data = res.data as LoanApiResponse;
        setLoan(data);

        const mapped: Payment[] = (data.schedules ?? []).map((s) => {
          const total =
            Number(String(s.total_due ?? "0").replace(/[,₹\s]/g, "")) || 0;
          const paid =
            Number(
              String((s as any).paid_amount ?? "0").replace(/[,₹\s]/g, ""),
            ) || 0;

          const pendingFromApi = (s as any).pending_amount;
          const pendingNum =
            pendingFromApi != null
              ? Number(String(pendingFromApi).replace(/[,₹\s]/g, "")) ||
                Math.max(total - paid, 0)
              : Math.max(total - paid, 0);

          const remaining = pendingNum > 0 ? pendingNum : 0;

          let isLate = false;
          if (s.due_date) {
            const due = new Date(s.due_date);
            if (!Number.isNaN(due.getTime())) {
              const today = new Date();
              const dueDateOnly = new Date(
                due.getFullYear(),
                due.getMonth(),
                due.getDate(),
              );
              const todayDateOnly = new Date(
                today.getFullYear(),
                today.getMonth(),
                today.getDate(),
              );
              if (remaining > 0 && dueDateOnly < todayDateOnly) {
                isLate = true;
              }
            }
          }

          const baseStatus = mapPaymentStatus(
            s.payment_status,
            (s as any).paid_amount,
            s.total_due,
          );
          const status: PaymentStatus = isLate ? "Late" : baseStatus;

          return {
            dueDate: fmtDate(s.due_date),
            amount: fmtCurrency(s.total_due),
            totalPaid: fmtCurrency((s as any).paid_amount),
            status,
            paymentDate: s.payment_on ? fmtDate(s.payment_on) : null,
            raw: s,
            remainingAmount:
              status === "Partially Paid" || status === "Late"
                ? fmtCurrency(remaining)
                : undefined,
            remainingNumeric:
              status === "Partially Paid" || status === "Late"
                ? remaining
                : undefined,
          };
        });

        const filtered = mapped.filter((p) => {
          return (
            p.status === "Paid" ||
            p.status === "Upcoming" ||
            p.status === "Partially Paid" ||
            p.status === "Late" ||
            p.status === "Late Paid"
          );
        });

        setPayments(filtered);
      } catch (err: any) {
        const msg =
          (err as AxiosError)?.message ??
          (err?.toString && err.toString()) ??
          "Failed to fetch loan";
        setError(msg);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [loan_id, settlementSuccess]);

  /* ---------- Small local helpers ---------- */
  const parseNonNeg = (s: string) => {
    const n = Number(String(s).replace(/,/g, ""));
    if (!Number.isFinite(n) || Number.isNaN(n)) return 0;
    return n < 0 ? 0 : n;
  };

  // computed payable (if settlement.total_due is null treat as 0)
  const totalDueNumeric =
    settlement && typeof settlement.settlement_amount === "number"
      ? settlement.settlement_amount
      : settlement && settlement.settlement_amount != null
        ? Number(settlement.settlement_amount) || 0
        : 0;

  const discount = parseNonNeg(discountStr);
  const additional = parseNonNeg(additionalStr);
  const payableNumeric = Math.max(0, totalDueNumeric - discount + additional); // clamp >= 0

  /* ---------- Handlers passed to children (useCallback style kept inline for clarity) ---------- */

  const handlePay = (payment: Payment) => {
    if (isCustomer) {
      toast.error("You are not allowed to perform payments from this screen.");
      return;
    }

    navigate("/payments/single", {
      state: {
        payment,
        prefillAmountNumeric: payment.remainingNumeric ?? null,
        loan_code: loan?.loan_code,
      },
    });
    // console.log(loan?.loan_code);
  };

  const handleEdit = () => {
    setShowEditLoanModal(true);
  };

  const handleEditLoanSuccess = () => {
    // Reload the loan data after successful edit
    if (!loan_id) return;

    const loadLoan = async () => {
      try {
        const res = await getLoan(loan_id);
        if (res && res.success && res.data) {
          setLoan(res.data as any);
        }
      } catch (err) {
        console.error("Failed to reload loan data", err);
      }
    };

    loadLoan();
  };

  const handleOpenDeleteModal = (item: any) => {
    setSelectedForDelete(item);
    setConfirmDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setConfirmDeleteModalOpen(false);
    setSelectedForDelete(null);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      const res = await deleteLoan(loan_id);
      toast.success("Deleted successfully.");
      navigate(-1);
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete.");
    } finally {
      setDeleting(false);
      setConfirmDeleteModalOpen(false);
      setSelectedForDelete(null);
    }
  };

  const handleSettlement = async () => {
    setShowSettlement(true);
    setSettlementError(null);
    setSettlementSuccess(null);
    try {
      const res = await getBalanceDue(loan_id);
      if (res && res.success && res.data) {
        setSettlement(res.data as Settlement);
      } else if (res && typeof (res as any).total_due !== "undefined") {
        setSettlement(res as unknown as Settlement);
      } else {
        setSettlement(res as unknown as Settlement);
      }
    } catch (err) {
      setSettlementError("Failed to load settlement data.");
    }
  };

  const handleProcessSettlement = async () => {
    setSettlementError(null);
    setSettlementSuccess(null);

    if (!settlement) {
      setSettlementError("No settlement data available.");
      return;
    }
    if (!loan?.loan_id) {
      setSettlementError("Missing loan id.");
      return;
    }

    const discountN = parseNonNeg(discountStr);
    const additionalN = parseNonNeg(additionalStr);
    const totalDue = totalDueNumeric;
    const payable = Math.max(0, totalDue - discountN + additionalN);

    let paidOn = paidOnStr;
    if (!paidOn || Number.isNaN(new Date(paidOn).getTime())) {
      const t = new Date();
      paidOn = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    }

    const payload = {
      paid_on: paidOn,
      settlement_amount: Math.round(payable * 100) / 100,
      additional_amount: Math.round(additionalN * 100) / 100,
      adjustment_amount: Math.round(discountN * 100) / 100,
    };

    try {
      setProcessingSettlement(true);
      const res = await settleLoan(loan?.loan_id, payload);

      if (!res || !res.success) {
        const message = res?.message ?? "Failed to process settlement";
        setSettlementError(String(message));
        return;
      }

      setSettlementSuccess("Settlement processed successfully.");

      // refresh loan data
      try {
        const refreshed = await getLoan(loan_id);
        if (refreshed?.success) {
          setLoan(refreshed.data as LoanApiResponse);
        }
      } catch {
        // ignore
      }

      setTimeout(() => {
        setShowSettlement(false);
        setSettlementSuccess(null);
      }, 700);
    } catch (err: any) {
      setSettlementError(err?.message ?? "Failed to process settlement");
    } finally {
      setProcessingSettlement(false);
    }
  };

  const handleModalClose = () => {
    setShowTransactionModal(false);
    setSelectedTransaction(null);
    setSelectedTransactions([]);
  };

  const handleDownloadReceipt = async (tx: Transaction | null) => {
    if (!tx) return;
    setDownloadingId(tx.transaction_id);
    setDownloading(true);
    try {
      const res = await getTransactionReceipt(tx.transaction_id);
      if (!res.success)
        throw new Error(res.message ?? "Failed to download receipt");

      const blob = res.blob as Blob;

      const filenameFromHeader = (() => {
        try {
          const cd =
            res.headers?.["content-disposition"] ||
            res.headers?.["Content-Disposition"] ||
            "";
          const m1 = /filename\\*=UTF-8''(.+)$/.exec(cd);
          if (m1 && m1[1]) return decodeURIComponent(m1[1]);
          const m2 = /filename=\"?([^\";]+)\"?/.exec(cd);
          if (m2 && m2[1]) return m2[1];
        } catch (e) {
          // ignore
        }
        return `receipt_${tx.transaction_id}.pdf`;
      })();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromHeader || `receipt_${tx.transaction_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err?.message ?? "Failed to download receipt");
    } finally {
      setDownloading(false);
      setDownloadingId(null);
    }
  };

  const handleViewTransaction = (scheduleId?: number | null) => {
    if (!loan) return;

    if (!scheduleId) {
      setSelectedTransactions(loan.transactions ?? []);
      setSelectedTransaction(null);
      setShowTransactionModal(true);
      return;
    }

    const txs = loan.transactions ?? [];
    const matches = txs
      .filter((t) => t.schedule_id === scheduleId)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

    setSelectedTransactions(matches);
    setSelectedTransaction(matches.length > 0 ? matches[0] : null);
    setShowTransactionModal(true);
  };

  const openVehicleModal = useCallback(async (id?: string | number | null) => {
    setVehicleError(null);
    setVehicleData(null);
    setShowVehicleModal(true);

    if (!id) {
      setVehicleError("Vehicle ID not available.");
      setVehicleLoading(false);
      return;
    }

    setVehicleLoading(true);
    try {
      const res = await getVehicle(id);

      // Handle multiple possible service response shapes:
      // 1) { vehicle: { ... } }
      // 2) { data: { vehicle: { ... } } }
      // 3) { success: true, data: { ... } } (vehicle object inside data)
      // 4) raw vehicle object directly
      let maybeVehicle: any = null;

      if (!res) {
        throw new Error("Empty response from getVehicle");
      }

      // common: res.vehicle
      if (res.vehicle) {
        maybeVehicle = res.vehicle;
      }
      // common: res.data.vehicle
      else if (res.data && res.data.vehicle) {
        maybeVehicle = res.data.vehicle;
      }
      // if res.data looks like the vehicle directly
      else if (
        res.data &&
        typeof res.data === "object" &&
        !res.data.success &&
        Object.keys(res.data).length > 0
      ) {
        // heuristically treat res.data as vehicle if it has registration_number or vehicle_id
        if (res.data.registration_number || res.data.vehicle_id)
          maybeVehicle = res.data;
      }
      // if the service returns success/data wrapper where data is the vehicle object
      else if (res.success && res.data) {
        maybeVehicle = res.data;
      }
      // finally, maybe res itself is the vehicle
      else {
        maybeVehicle = res;
      }

      // final check - if still null, throw so we show error
      if (
        !maybeVehicle ||
        (typeof maybeVehicle === "object" &&
          Object.keys(maybeVehicle).length === 0)
      ) {
        throw new Error("Vehicle not found in response");
      }

      setVehicleData(maybeVehicle);
    } catch (err: any) {
      console.error("getVehicle failed:", err);
      setVehicleError(err?.message ?? "Failed to fetch vehicle details.");
    } finally {
      setVehicleLoading(false);
    }
  }, []);

  // total outstanding across schedules (number)
  const totalRemainingNumeric = (loan?.schedules ?? []).reduce((acc, s) => {
    const total =
      Number(String(s.total_due ?? "0").replace(/[,₹\s]/g, "")) || 0;
    const paid =
      Number(String((s as any).paid_amount ?? "0").replace(/[,₹\s]/g, "")) || 0;

    const pendingFromApi = (s as any).pending_amount;
    const pendingNum =
      pendingFromApi != null
        ? Number(String(pendingFromApi).replace(/[,₹\s]/g, "")) ||
          Math.max(total - paid, 0)
        : Math.max(total - paid, 0);

    return acc + Math.max(0, pendingNum);
  }, 0);

  const totalRemainingDisplay = fmtCurrency(totalRemainingNumeric);

  const handleDownloadPagePdf = async () => {
    if (!pdfRef.current) return;

    const element = pdfRef.current;

    const opt = {
      margin: [5, 5, 5, 5],
      filename: `Loan_${loan?.loan_code}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true, // <--- Change this from false to true
        allowTaint: false, // <--- Set to false (better for CORS)
        windowWidth: 794,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
    };

    html2pdf()
      .set(opt as any)
      .from(element)
      .save();
  };

  const handleSettlementReceiptDownload = async () => {
    if (isDownloading) return;

    setIsDownloading(true);

    try {
      const res = await getloanSettlementReceipt(loan_id);

      if (!res || !res.receipt || !res.transaction) {
        throw new Error("Invalid settlement receipt response");
      }

      loanSettlementRef.current = res;
      setLoanSettlement(res);

      // Wait for React DOM render
      await new Promise(requestAnimationFrame);

      if (!pdfRefSettlement.current) return;

      const element = pdfRefSettlement.current;

      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Loan_${loan?.loan_code || loan?.loan_id}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 1.5,
          useCORS: true,
          scrollX: 0,
          scrollY: 0,
          windowWidth: 1123,
          windowHeight: 794,
        },
        jsPDF: {
          unit: "mm",
          format: "a4",
          orientation: "landscape",
        },
        pagebreak: { mode: [] },
      };

      const worker = html2pdf()
        .set(opt as any)
        .from(element);

      const pdf: any = await worker.toPdf().get("pdf");

      // Remove extra pages
      while (pdf.internal.getNumberOfPages() > 1) {
        pdf.deletePage(pdf.internal.getNumberOfPages());
      }

      pdf.save(`Loan_${loan?.loan_code || loan?.loan_id}.pdf`);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load settlement receipt.");
    } finally {
      setIsDownloading(false);
    }
  };

  const firstSchedule = loan?.schedules?.[0];

  return (
    <div className="p-3 light-bg-color ">
      <div className="hidden">
        {/* PDF */}
        <div ref={pdfRef} className="pdf-safe ">
          <div className="pdf-page">
            <div className="pdf-content relative overflow-hidden">
              {/* HEADER */}
              <div className="pdf-header">
                {/* LEFT */}
                <div className="company-section">
                  <div className="company-top">
                    <img src={Logo} alt="logo" className="company-logo" />

                    <div>
                      <h2>Shri Renga Finance</h2>
                      <p className="muted">
                        Generated on: {new Date().toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* ADDRESS ROW */}
                  <div className="address-row">
                    <div className="address-box">
                      <p>
                        10/27, Railway Feeder Road,
                        <br />
                        South of Post Office,
                        <br />
                        Muthukrishnapuram,
                        <br />
                        Kadayanallur - 627751
                        <br />
                        OFFICE: 9952470049
                        <br />
                        GPAY: 8220583664
                      </p>
                    </div>

                    <div className="address-divider" />

                    <div className="address-box">
                      <p>
                        18/7/1, Sivakurunatha Puram,
                        <br />
                        Sankarankovil Main Road,
                        <br />
                        Surandai - 627859
                        <br />
                        OFFICE: 7418275099
                        <br />
                        GPAY: 7418533995
                      </p>
                    </div>
                  </div>

                  {/* MOBILE NUMBERS */}
                  {/* <div className="contact-row">
                    <p>GPay: 8220583664 ,7418533995</p>
                  </div> */}
                </div>

                {/* RIGHT */}
                <div className="customer-section">
                  <img
                    src={loan?.borrower?.profile_photo_url || "fallback_url"}
                    crossOrigin="anonymous" // <--- Add this
                    className="customer-image"
                    alt="Customer"
                  />

                  <div className="loan-info">
                    <p>
                      <strong>Loan Code:</strong> {loan?.loan_code}
                    </p>

                    <p>
                      <strong>Customer Code:</strong>{" "}
                      {loan?.borrower?.customer_code}
                    </p>
                  </div>
                </div>
              </div>

              {/* CUSTOMER + LOAN INFO */}
              <div className="info-grid">
                <div className="info-box">
                  <h4>Customer Details</h4>
                  <p>
                    <span>Name</span>
                    {loan?.borrower?.first_name} {loan?.borrower?.last_name}
                  </p>
                  <p>
                    <span>Mobile Number</span>
                    {loan?.borrower?.mobile_number}
                  </p>
                  <p>
                    <span>Vehicle No</span>
                    {loan?.vehicle?.registration_number}
                  </p>
                  <p>
                    <span>Vehicle </span>
                    {loan?.vehicle?.model}( {loan?.vehicle?.year_of_manufacture}
                    )
                  </p>
                </div>

                <div className="info-box">
                  <h4>Loan Summary</h4>
                  <p>
                    <span>Principal</span>
                    {fmtCurrency(loan?.loan_amount)}
                  </p>
                  {/* <p>
                    <span>Interest</span>
                    {loan?.interest_rate}% p.a
                  </p> */}
                  <p>
                    <span>EMI</span>
                    {fmtCurrency(loan?.emi_amount)}
                  </p>
                  <p>
                    <span>Tenure</span>
                    {loan?.tenure_months} Months
                  </p>
                  <p>
                    <span>Total Due</span>
                    {fmtCurrency(loan?.total_amount)}
                  </p>
                </div>
              </div>

              {/* PAGE BREAK BEFORE TABLE */}
              {/* <div className="page-break" /> */}

              {/* DUE PAYMENTS TABLE */}
              <h4 className="section-title">Due Payments</h4>

              <table className="pdf-table">
                <thead>
                  <tr>
                    <th>S.no</th>
                    <th>Due Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Paid</th>
                    <th>Payment Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{p.dueDate}</td>
                      <td>{p.amount}</td>
                      <td>{p.status}</td>
                      <td>{p.totalPaid}</td>
                      <td>{p.paymentDate ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div ref={pdfRefSettlement} className="pdf-safe-settle ">
          <div className="pdf-page-settle ">
            <div className="pdf-columns-settle ">
              {/* COLUMN 1 → YOUR CONTENT */}
              <div className="pdf-column-settle ">
                <div className="pdf-content-settle pr-2 ">
                  <div className=" text-center flex justify-center w-full">
                    <img className="size-24" src={Logo} alt="" />
                  </div>
                  {/* HEADER */}

                  <h2 className="font-bold text-xl">Shri Renga Finance</h2>
                  <div className="address-row">
                    <div className="address-box">
                      <p>
                        10/27, Railway Feeder Road,
                        <br />
                        South of Post Office,
                        <br />
                        Muthukrishnapuram,
                        <br />
                        Kadayanallur - 627751
                        <br />
                        OFFICE: 9952470049
                        <br />
                        GPAY: 8220583664
                      </p>
                    </div>

                    <div className="address-divider" />

                    <div className="address-box">
                      <p>
                        18/7/1, Sivakurunatha Puram,
                        <br />
                        Sankarankovil Main Road,
                        <br />
                        Surandai - 627859
                        <br />
                        OFFICE: 7418275099
                        <br />
                        GPAY: 7418533995
                      </p>
                    </div>
                  </div>
                  {/* MOBILE NUMBERS */}
                  {/* <div className="contact-row pb-3">
                    <p>GPay: 8220583664 ,7418533995</p>
                  </div> */}
                  <hr />
                  {/* <p className="muted">
                    Generated on: {new Date().toLocaleDateString()}
                  </p> */}
                  <div className="text-right mb-3 pr-2">
                    <p>
                      <strong>Loan Code:</strong> {loan?.loan_code}
                    </p>
                    <p>
                      <strong>Customer Code:</strong>{" "}
                      {loan?.borrower?.customer_code}
                    </p>
                  </div>

                  {/* CUSTOMER + LOAN INFO */}
                  <div className="info-grid pr-2">
                    <div className="info-box">
                      <h4>Customer Details</h4>
                      <p>
                        <span>Name</span>
                        {loan?.borrower?.first_name} {loan?.borrower?.last_name}
                      </p>
                      <p>
                        <span>Mobile Number</span>
                        {loan?.borrower?.mobile_number}
                      </p>
                      <p>
                        <span>Vehicle No</span>
                        {loan?.vehicle?.registration_number}
                      </p>
                      <p>
                        <span>Vehicle </span>
                        {loan?.vehicle?.model}({" "}
                        {loan?.vehicle?.year_of_manufacture})
                      </p>
                    </div>

                    <div className="info-box">
                      <h4>Loan Summary</h4>
                      <p>
                        <span>Principal</span>
                        {fmtCurrency(loan?.loan_amount)}
                      </p>
                      <p>
                        <span>EMI</span>
                        {fmtCurrency(loan?.emi_amount)}
                      </p>
                      <p>
                        <span>Tenure</span>
                        {loan?.tenure_months} Months
                      </p>
                      <p>
                        <span>Total Due</span>
                        {fmtCurrency(loan?.total_amount)}
                      </p>
                    </div>
                  </div>

                  <p className="mb-4">
                    <strong>Payment:</strong>
                  </p>
                  <hr />

                  <div className="mb-3">
                    <p>
                      <strong>Receipt No:</strong>{" "}
                      {settlementloan?.receipt?.receipt_no}
                    </p>
                    <p className="mb-4">
                      <strong>Settlement Date:</strong>
                      {fmtDate(settlementloan?.receipt?.settlement_date)}
                    </p>

                    <h4 className="mb-3">
                      <strong>Transaction Details</strong>
                    </h4>

                    {/* <p>Schedule Id :{settlementloan?.transaction?.schedule_id}</p> */}
                    <p>
                      Amount Paid: ₹{settlementloan?.transaction?.amount_paid}
                    </p>
                    <p>
                      Payment Mode: {settlementloan?.transaction?.payment_mode}
                    </p>
                    <p>Status : {settlementloan?.transaction?.status}</p>
                    <p>
                      Created At :
                      {fmtDate(settlementloan?.transaction?.created_at)}
                    </p>
                  </div>
                  <hr />
                </div>
              </div>

              {/* COLUMN 2 EMPTY */}
              <div className="pdf-column-settle "></div>

              {/* COLUMN 3 EMPTY */}
              <div className="pdf-column-settle "></div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between mb-3">
        <div className="flex items-center gap-3 ">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-white hover:bg-gray-200 text-gray-700 font-medium shadow-sm transition-all duration-150 cursor-pointer"
          >
            <Icon icon="mdi:arrow-left" width={18} />
            <span className="hidden md:block " ref={menuRef}>
              Back
            </span>
          </button>

          <h3 className="text-lg font-semibold text-color">Customer Details</h3>
        </div>

        <div className="flex gap-1 md:gap-4 ">
          <Button variant="secondary" onClick={handleDownloadPagePdf}>
            <span className="hidden md:flex">Download PDF</span>
            <Icon
              className="-hidden"
              icon="line-md:download"
              width="24"
              height="24"
            ></Icon>
          </Button>

          {isAdmin && (
            <Button
              variant="danger"
              onClick={() => handleOpenDeleteModal(loan_id)}
            >
              <span className="hidden md:flex"> Delete</span>
              <Icon icon="mdi:delete" width={16} />
            </Button>
          )}
        </div>
      </div>

      <div className="mx-auto space-y-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <span className="animate-spin mr-2">
              <svg
                className="h-6 w-6 text-sky-500"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v8z"
                ></path>
              </svg>
            </span>
            <span className="text-slate-500">Loading...</span>
          </div>
        ) : error ? (
          <div className="p-6 bg-red-50 rounded shadow-sm text-sm text-red-700">
            Error: {error}
          </div>
        ) : loan ? (
          <>
            {/* Top cards */}
            <LoanTopCards
              loan={loan}
              isAdmin={isAdmin}
              // onViewVehicle={() => {/* optional: navigate to vehicle page */ }}
              onDelete={() => handleOpenDeleteModal(loan_id)}
              onEdit={handleEdit}
              fmtCurrency={fmtCurrency}
              fmtDate={fmtDate}
              onViewVehicle={() =>
                openVehicleModal(loan?.vehicle?.vehicle_id ?? null)
              }
            />

            {/* Tabs */}
            <div className="flex border-b border-indigo-200 space-x-6 overflow-x-auto">
              {[
                { key: "due", label: "Due Payments" },
                { key: "docs", label: "Documents" },
                { key: "nominees", label: "Nominees" },
                { key: "notes", label: "Notes" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`relative py-2 text-sm font-medium ${
                    activeTab === tab.key
                      ? "text-color after:absolute after:bottom-0 after:left-0 after:w-full after:h-[2px] after:bg-indigo-600"
                      : "text-gray-300 hover:text-gray-700 cursor-pointer"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Due Tab */}
            {activeTab === "due" && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex grid md:grid-cols-2 gap-3 md:gap-0 md:items-center">
                  <div>
                    <h3 className="text-lg font-semibold">Due Payments</h3>
                    <div className="text-sm text-gray-500">
                      Loan status:{" "}
                      <span className="font-medium">{loan.status}</span>
                    </div>
                    {firstSchedule && (
                      <div className="text-sm  mr-1 font-semibold ">
                        <span className="main-color">
                          Principal:{" "}
                          {fmtCurrency(firstSchedule.principal_amount)}
                        </span>
                        <span className="mx-1">+</span>
                        <span className="text-red-500">
                          Interest: {fmtCurrency(firstSchedule.interest_amount)}
                        </span>
                        <span className="mx-1">=</span>
                        <span className="font-medium text-green-700">
                          {fmtCurrency(firstSchedule.total_due)}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="gap-3 flex items-center justify-between md:justify-end ">
                    <div className="text-sm text-gray-600">
                      <div className="flex flex-col  md:flex-row items-baseline gap-2  bg-[#f6f8fd]  text-gray-800 p-1 px-3 rounded-md">
                        <span className="text-gray-500">Remaining:</span>
                        <span className="text-lg font-semibold  text-red-600">
                          {totalRemainingDisplay}
                        </span>
                      </div>
                    </div>
                    {!isCustomer &&
                      loan?.status != "completed" &&
                      loan?.status != "Settlement" && (
                        <Button variant="secondary" onClick={handleSettlement}>
                          Settlement{" "}
                          <span className="hidden md:flex"> Payment</span>
                        </Button>
                      )}
                    {!isCustomer && loan?.status === "Settlement" && (
                      <Button
                        variant="secondary"
                        onClick={handleSettlementReceiptDownload}
                        disabled={isDownloading}
                      >
                        {isDownloading ? (
                          <span>Downloading…</span>
                        ) : (
                          <div className="flex">
                            <span>Settlement </span>
                            <span className="hidden md:flex pl-2">
                              {" "}
                              Receipt{" "}
                            </span>
                          </div>
                        )}

                        <Icon
                          className="-hidden"
                          icon="line-md:download"
                          width="24"
                          height="24"
                        ></Icon>
                      </Button>
                    )}
                  </div>
                </div>

                {/* Use the split table component */}
                <DuePaymentsTable
                  payments={payments}
                  isCustomer={isCustomer}
                  onPay={handlePay}
                  onViewTransaction={handleViewTransaction}
                  transactions={loan?.transactions ?? []}
                />
              </div>
            )}

            {activeTab === "docs" && (
              <LoanDocuments loanIdProp={loan?.loan_id ?? loan_id} />
            )}

            {activeTab === "nominees" && (
              <LoanNominees
                profileId={loan?.borrower?.profile_id ?? null}
                loanId={loan?.loan_id ?? loan_id}
              />
            )}
            {activeTab === "notes" && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="text-xs text-gray-500">Notes:</div>
                <div className="font-semibold">{loan?.notes ?? ""}</div>
              </div>
            )}
          </>
        ) : (
          <div className="p-6 bg-white rounded shadow-sm text-sm text-gray-600">
            No loan data found.
          </div>
        )}
      </div>

      {/* Transaction Modal (child component) */}
      <TransactionModal
        isOpen={showTransactionModal}
        onClose={handleModalClose}
        selectedTransactions={selectedTransactions}
        selectedTransaction={selectedTransaction}
        onSelect={(tx: any) => setSelectedTransaction(tx)}
        onDownload={handleDownloadReceipt}
        downloadingId={downloadingId}
        isCustomer={isCustomer}
      />

      {/* Settlement Modal (child component) */}
      <SettlementModal
        isOpen={showSettlement}
        onClose={() => setShowSettlement(false)}
        settlement={settlement}
        discountStr={discountStr}
        additionalStr={additionalStr}
        paidOnStr={paidOnStr}
        setDiscountStr={setDiscountStr}
        setAdditionalStr={setAdditionalStr}
        setPaidOnStr={setPaidOnStr}
        payableNumeric={payableNumeric}
        totalDueNumeric={totalDueNumeric}
        processingSettlement={processingSettlement}
        onProcess={handleProcessSettlement}
        error={settlementError}
        success={settlementSuccess}
        loanId={loan?.loan_id ?? loan_id}
        loan_code={loan?.loan_code ?? loan_id}
      />

      {/* Delete confirmation modal (child component) */}
      <DeleteConfirmModal
        isOpen={confirmDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        deleting={deleting}
      />

      {/* Vehicle details modal */}
      <Modal
        isOpen={showVehicleModal}
        onClose={() => setShowVehicleModal(false)}
        title="Vehicle Details"
      >
        {vehicleLoading ? (
          <div className="p-6">
            <p>Loading vehicle details…</p>
          </div>
        ) : vehicleError ? (
          <div className="p-6">
            <p className="text-red-600">Error: {vehicleError}</p>
          </div>
        ) : vehicleData ? (
          <OneVehicleDetails vehicle={vehicleData} />
        ) : (
          <div className="p-6 text-gray-600">No vehicle data available.</div>
        )}
      </Modal>

      {/* Edit Loan Modal */}
      {loan && (
        <EditLoanModal
          isOpen={showEditLoanModal}
          onClose={() => setShowEditLoanModal(false)}
          onSuccess={handleEditLoanSuccess}
          loanId={loan_id}
          initialData={{
            loan_amount: loan.loan_amount,
            interest_rate: loan.interest_rate,
            tenure_months: loan.tenure_months,
            start_date: loan.start_date,
            loan_created_date: loan.loan_created_date,
            document_charges: loan.document_charges,
          }}
        />
      )}
    </div>
  );
}
